'use client'

import { LazyMotion, domAnimation, m, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { LandingButton } from './landing-button'
import { CurveDecorativeIcon } from '@/components/icons'

// Optimized blur animation - uses word-level animation instead of character-level for better performance
function BlurText({ text, isMobile }: { text: string; isMobile: boolean }) {
    const words = text.split(' ')

    const container = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: isMobile ? 0.02 : 0.03,
                delayChildren: isMobile ? 0 : 0.01,
            },
        },
    }

    const wordVariant = {
        visible: {
            opacity: 1,
            y: 0,
            filter: isMobile ? 'blur(0px)' : 'blur(0px)',
            transition: isMobile
                ? { duration: 0.2 }
                : {
                      type: 'spring' as const,
                      damping: 20,
                      stiffness: 100,
                  },
        },
        hidden: {
            opacity: 0,
            y: isMobile ? 5 : 10,
            filter: isMobile ? 'blur(0px)' : 'blur(8px)',
            transition: isMobile
                ? { duration: 0.2 }
                : {
                      type: 'spring' as const,
                      damping: 20,
                      stiffness: 100,
                  },
        },
    }

    return (
        <m.span
            style={{ display: 'inline', willChange: 'opacity, transform' }}
            variants={container}
            initial="hidden"
            animate="visible"
        >
            {words.map((word, wordIndex) => (
                <m.span
                    key={wordIndex}
                    style={{
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        willChange: 'opacity, transform, filter',
                    }}
                    variants={wordVariant}
                >
                    {word}
                    {wordIndex < words.length - 1 && '\u00A0'}
                </m.span>
            ))}
        </m.span>
    )
}

export function LandingHero() {
    const texts = [
        { line1: 'Research Stock Without', line2: 'Switching Tabs' },
        { line1: 'Research Stock With', line2: 'AI in sec' },
        { line1: 'Find Next Multi bagger', line2: 'with AI' },
        { line1: 'Now you can also Get', line2: '5X Returns on Stocks' },
    ]

    const [currentIndex, setCurrentIndex] = useState(0)
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % texts.length)
        }, 8000) // Change text every 8 seconds

        return () => clearInterval(interval)
    }, [texts.length])

    useEffect(() => {
        // Detect mobile device
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    return (
        <LazyMotion features={domAnimation}>
            <div className="min-h-[40vh] mx-auto flex flex-col items-center justify-start py-20">
                {/* Hero Section */}
                <div className="max-w-[591px] w-full text-center space-y-5">
                    <h1 className="mt-[14%] text-[26px] font-bold md:text-5xl lg:text-[47px] text-black dark:text-white leading-tight flex items-center justify-center min-h-[80px] md:min-h-0">
                        <AnimatePresence mode="wait">
                            <m.div
                                key={currentIndex}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <BlurText
                                    text={texts[currentIndex]?.line1 || ''}
                                    isMobile={isMobile}
                                />
                                <br />
                                <BlurText
                                    text={texts[currentIndex]?.line2 || ''}
                                    isMobile={isMobile}
                                />
                            </m.div>
                        </AnimatePresence>
                    </h1>

                    <div className="flex justify-center items-center gap-2.5 flex-col">
                        <Link href="/auth" target="_blank" rel="noopener noreferrer">
                            <LandingButton variant="blue">Try For Free</LandingButton>
                        </Link>
                        <span className="hidden sm:inline text-footnote text-black/40 dark:text-white/40">
                            No Credit Card Needed
                        </span>
                    </div>
                </div>

                {/* Content Card */}
                <div className="max-w-4xl w-full px-2.5 md:px-0">
                    <video
                        src="https://wlrmvbskipy8hftc.public.blob.vercel-storage.com/videos/Company%20analysis.mp4"
                        className="w-full h-auto border-[4px] border-white/20 rounded-lg"
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls={false}
                        preload="auto"
                        poster="/landing/video-poster-hero.jpg"
                        {...({
                            fetchPriority: 'high',
                        } as React.VideoHTMLAttributes<HTMLVideoElement>)}
                    />
                </div>
            </div>
        </LazyMotion>
    )
}

export function FooterHero() {
    return (
        <>
            {/* Decorative Icon - Mobile Only */}
            <div className="block md:hidden w-full px-5 py-10">
                <CurveDecorativeIcon className="w-[70%] mx-auto" />
            </div>

            <section className="min-h-[960px] md:min-h-[60vh] lg:min-h-[40vh] bg-gradient-to-br from-[#7CB9E8] to-[#4A90E2] flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-[#ffffff] text-[26px] md:text-5xl lg:text-6xl font-bold mb-5">
                        Get Insighthread Free
                    </h1>
                    <div className="flex justify-center items-center gap-2.5 flex-col">
                        <Link href="/auth" target="_blank" rel="noopener noreferrer">
                            <LandingButton variant="white">Try For Free</LandingButton>
                        </Link>
                        <span className="text-footnote text-white/80 hidden md:inline">
                            No Credit Card Needed
                        </span>
                    </div>
                </div>
            </section>
        </>
    )
}
